import { useState } from 'react';
import { api } from '../lib/api';
import { useAiQuery, useVehicles } from '../lib/queries';
import { Badge, Button, Card, Field, Input, PageHeader, Select, Spinner } from '../components/ui';
import { Icon } from '../components/icons';

const SUGGESTIONS = [
  'Quais carros estão parados há mais de 7 dias?',
  'Que peças preciso comprar?',
  'Quais vencimentos estão próximos?',
  'Resumo geral da garagem',
];

interface Turn {
  pergunta: string;
  resposta: string;
  fonte: string;
  dados: string[];
}

export function AssistantPage() {
  const ask = useAiQuery();
  const { data: vehicles } = useVehicles();
  const [pergunta, setPergunta] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);

  const send = async (q: string) => {
    if (!q.trim()) return;
    setPergunta('');
    const res = await ask.mutateAsync({ pergunta: q });
    setTurns((t) => [{ pergunta: q, resposta: res.resposta, fonte: res.fonte, dados: res.dadosConsultados }, ...t]);
  };

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Assistente"
        subtitle="Pergunte sobre a garagem em linguagem natural"
        actions={<Badge color="#c8ff00">{ask.data?.fonte === 'claude' ? 'Claude' : 'mock'}</Badge>}
      />

      <Card className="mb-4">
        <div className="flex gap-2">
          <Input
            value={pergunta}
            onChange={(e) => setPergunta(e.target.value)}
            placeholder="Ex.: quais carros estão parados há mais de 7 dias?"
            onKeyDown={(e) => e.key === 'Enter' && send(pergunta)}
          />
          <Button onClick={() => send(pergunta)} disabled={ask.isPending || !pergunta.trim()}>
            {ask.isPending ? <Spinner className="h-4 w-4" /> : <Icon name="sparkles" className="h-4 w-4" />}
            Perguntar
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              className="text-xs text-muted hover:text-accent border border-border rounded-full px-3 py-1 transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      </Card>

      <div className="space-y-3">
        {turns.map((t, i) => (
          <Card key={i}>
            <div className="text-sm text-muted mb-2 flex items-center gap-2">
              <Icon name="sparkles" className="h-4 w-4 text-accent" /> {t.pergunta}
            </div>
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{t.resposta}</p>
            {t.dados.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {t.dados.map((d) => (
                  <span key={d} className="text-[11px] text-muted bg-surface-2 rounded px-2 py-0.5 num">{d}</span>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      {vehicles && vehicles.length > 0 && <DiagnosePanel vehicleOptions={vehicles} />}
    </div>
  );
}

function DiagnosePanel({ vehicleOptions }: { vehicleOptions: { id: string; apelido: string }[] }) {
  return (
    <Card className="mt-6">
      <h2 className="font-semibold mb-1 flex items-center gap-2">
        <Icon name="wrench" className="h-4 w-4 text-accent" /> Diagnóstico por veículo
      </h2>
      <p className="text-xs text-muted mb-3">Raciocínio sobre o histórico de OS e sintomas.</p>
      <DiagnoseForm vehicleOptions={vehicleOptions} />
    </Card>
  );
}

function DiagnoseForm({ vehicleOptions }: { vehicleOptions: { id: string; apelido: string }[] }) {
  const [vehicleId, setVehicleId] = useState(vehicleOptions[0]?.id ?? '');
  const [sintomas, setSintomas] = useState('');
  const [result, setResult] = useState<{ diagnostico: string; proximosPassos: string[] } | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post<{ diagnostico: string; proximosPassos: string[] }>('/ai/diagnose', {
        vehicleId,
        sintomas: sintomas || undefined,
      });
      setResult(res);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Veículo">
          <Select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
            {vehicleOptions.map((v) => (
              <option key={v.id} value={v.id}>{v.apelido}</option>
            ))}
          </Select>
        </Field>
        <Field label="Sintomas (opcional)">
          <Input value={sintomas} onChange={(e) => setSintomas(e.target.value)} placeholder="faróis apagando…" />
        </Field>
      </div>
      <Button onClick={run} disabled={loading || !vehicleId}>
        {loading ? 'Analisando…' : 'Diagnosticar'}
      </Button>
      {result && (
        <div className="bg-surface-2 rounded-lg p-3 text-sm space-y-2">
          <p className="whitespace-pre-wrap">{result.diagnostico}</p>
          {result.proximosPassos.length > 0 && (
            <ul className="list-disc pl-5 text-muted">
              {result.proximosPassos.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
